import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiUsers, FiArrowRight } from 'react-icons/fi';
import { GiTrophy, GiCrossedSwords } from 'react-icons/gi';
import PlayerAvatar from './PlayerAvatar';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { comparePlayers } from '../services/api';
import './CompareMode.css';

/**
 * Compare mode component for comparing two players
 */
const CompareMode = () => {
  const [player1Input, setPlayer1Input] = useState('');
  const [player2Input, setPlayer2Input] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCompare = async () => {
    if (!player1Input.trim() || !player2Input.trim()) return;

    setLoading(true);
    setError(null);
    setComparisonData(null);

    try {
      const data = await comparePlayers(player1Input.trim(), player2Input.trim());
      setComparisonData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => num?.toLocaleString() || '0';

  // Compare stats and determine winner
  const getWinner = (stat1, stat2) => {
    const num1 = parseFloat(stat1) || 0;
    const num2 = parseFloat(stat2) || 0;
    if (num1 > num2) return 1;
    if (num2 > num1) return 2;
    return 0;
  };

  const compareStats = [
    { label: 'Network Level', key: 'networkLevel', format: (v) => v },
    { label: 'Achievement Points', key: 'achievementPoints', format: formatNumber },
    { label: 'Karma', key: 'karma', format: formatNumber },
    { label: 'BW Level', key: 'bedwars.level', format: (v) => v },
    { label: 'BW FKDR', key: 'bedwars.fkdr', format: (v) => v },
    { label: 'BW Wins', key: 'bedwars.wins', format: formatNumber },
    { label: 'SW Level', key: 'skywars.level', format: (v) => v },
    { label: 'SW KDR', key: 'skywars.kdr', format: (v) => v },
    { label: 'SW Wins', key: 'skywars.wins', format: formatNumber },
    { label: 'Duels Wins', key: 'duels.wins', format: formatNumber },
    { label: 'Duels WLR', key: 'duels.wlr', format: (v) => v }
  ];

  // Get nested value from object
  const getValue = (obj, path) => {
    return path.split('.').reduce((o, k) => o?.[k], obj);
  };

  return (
    <div className="compare-mode">
      <div className="compare-header">
        <div className="compare-title">
          <FiUsers />
          <h2>Compare Players</h2>
        </div>
        <p className="compare-subtitle">Enter two usernames to compare their stats</p>
      </div>

      <div className="compare-inputs">
        <div className="compare-input-group">
          <label>Player 1</label>
          <input
            type="text"
            placeholder="Username"
            value={player1Input}
            onChange={(e) => setPlayer1Input(e.target.value)}
            maxLength={16}
          />
        </div>

        <div className="compare-vs">VS</div>

        <div className="compare-input-group">
          <label>Player 2</label>
          <input
            type="text"
            placeholder="Username"
            value={player2Input}
            onChange={(e) => setPlayer2Input(e.target.value)}
            maxLength={16}
          />
        </div>

        <motion.button
          className="compare-button"
          onClick={handleCompare}
          disabled={!player1Input.trim() || !player2Input.trim() || loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiSearch />
          Compare
        </motion.button>
      </div>

      {/* Loading */}
      {loading && <LoadingSpinner />}

      {/* Error */}
      {error && <ErrorMessage message={error} onRetry={() => setError(null)} />}

      {/* Comparison Results */}
      <AnimatePresence>
        {comparisonData && (
          <motion.div
            className="comparison-results"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
          >
            {/* Player Headers */}
            <div className="comparison-players">
              <div className="comparison-player">
                <PlayerAvatar
                  uuid={comparisonData.player1.uuid}
                  username={comparisonData.player1.username}
                  size="medium"
                />
                <h3>{comparisonData.player1.username}</h3>
                <span
                  className="player-rank-badge"
                  style={{ backgroundColor: comparisonData.player1.rankColor }}
                >
                  {comparisonData.player1.rank}
                </span>
              </div>

              <div className="vs-divider">
                <GiCrossedSwords />
              </div>

              <div className="comparison-player">
                <PlayerAvatar
                  uuid={comparisonData.player2.uuid}
                  username={comparisonData.player2.username}
                  size="medium"
                />
                <h3>{comparisonData.player2.username}</h3>
                <span
                  className="player-rank-badge"
                  style={{ backgroundColor: comparisonData.player2.rankColor }}
                >
                  {comparisonData.player2.rank}
                </span>
              </div>
            </div>

            {/* Stats Comparison Table */}
            <div className="comparison-table">
              {compareStats.map((stat, index) => {
                const val1 = getValue(comparisonData.player1, stat.key);
                const val2 = getValue(comparisonData.player2, stat.key);
                const winner = getWinner(val1, val2);

                return (
                  <motion.div
                    key={stat.key}
                    className="comparison-row"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className={`stat-value ${winner === 1 ? 'winner' : ''}`}>
                      {winner === 1 && <GiTrophy className="trophy" />}
                      {stat.format(val1)}
                    </div>
                    <div className="stat-label">{stat.label}</div>
                    <div className={`stat-value ${winner === 2 ? 'winner' : ''}`}>
                      {stat.format(val2)}
                      {winner === 2 && <GiTrophy className="trophy" />}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompareMode;
